import React, { ComponentProps } from 'react';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Hint } from '../../../_lib/components/Hint';
import { Heading } from '../../../_lib/components/Heading/Heading';

type FormApplicationSettingsDefaultProps = {
  emailAddress1?: string;
  emailAddress2?: string;
  emailAddress3?: string;
  replyToAddress?: string;
  notifyCreatorPortal?: boolean;
  notifyCreatorEmail?: boolean;
  notifyManagersPortal?: boolean;
  notifyManagersEmail?: boolean;
};

function FormApplicationSettingsDefault({
  emailAddress1,
  emailAddress2,
  emailAddress3,
  replyToAddress,
  notifyCreatorPortal,
  notifyCreatorEmail,
  notifyManagersPortal,
  notifyManagersEmail,
}: FormApplicationSettingsDefaultProps) {
  const { t } = useSonTranslation('FormApplicationSettings');

  const hasNotificationOptions =
    notifyCreatorPortal || notifyCreatorEmail || notifyManagersPortal || notifyManagersEmail;
  const hasEmailAddresses = Boolean(emailAddress1 || emailAddress2 || emailAddress3 || replyToAddress);

  if (!hasNotificationOptions && !hasEmailAddresses) {
    return (
      <div className="form-application-settings__default-container">
        <Hint message={t('noEmailAddressesAvailableHint')} />
      </div>
    );
  }

  return (
    <div className="form-application-settings__default-container">
      {hasNotificationOptions && (
        <>
          <Heading hierarchy={5} className="form-application-settings__default-heading font-bold-16-19">
            {t('form.notificationsFieldsetLegend')}
          </Heading>
          <ul className="form-application-settings__active-options-list">
            {notifyCreatorPortal && (
              <li className="form-application-settings__active-options-list-item">
                {t('form.notifyCreatorPortalLabel')} {t('form.notifyCreatorPortalHint')}
              </li>
            )}
            {notifyCreatorEmail && (
              <li className="form-application-settings__active-options-list-item">
                {t('form.notifyCreatorEmailLabel')} {t('form.notifyCreatorEmailHint')}
              </li>
            )}
            {notifyManagersPortal && (
              <li className="form-application-settings__active-options-list-item">
                {t('form.notifyManagersPortalLabel')} {t('form.notifyManagersPortalHint')}
              </li>
            )}
            {notifyManagersEmail && (
              <li className="form-application-settings__active-options-list-item">
                {t('form.notifyManagersEmailLabel')} {t('form.notifyManagersEmailHint')}
              </li>
            )}
          </ul>
        </>
      )}
      {hasEmailAddresses && (
        <>
          <Heading hierarchy={5} className="form-application-settings__default-heading font-bold-16-19">
            {t('form.fieldsetLegend')}
          </Heading>
          <dl className="form-application-settings__description-list">
            {emailAddress1 && (
              <>
                <dt className="form-application-settings__description-list-term font-bold-16-19">
                  {t('form.emailAddress1Label')}:
                </dt>
                <dd className="form-application-settings__description-list-description font-regular-16-19">
                  {emailAddress1}
                </dd>
              </>
            )}
            {emailAddress2 && (
              <>
                <dt className="form-application-settings__description-list-term font-bold-16-19">
                  {t('form.emailAddress2Label')}:
                </dt>
                <dd className="form-application-settings__description-list-description font-regular-16-19">
                  {emailAddress2}
                </dd>
              </>
            )}
            {emailAddress3 && (
              <>
                <dt className="form-application-settings__description-list-term font-bold-16-19">
                  {t('form.emailAddress3Label')}:
                </dt>
                <dd className="form-application-settings__description-list-description font-regular-16-19">
                  {emailAddress3}
                </dd>
              </>
            )}
            {replyToAddress && (
              <>
                <dt className="form-application-settings__description-list-term font-bold-16-19">
                  {t('form.replyToAddressLabel')}:
                </dt>
                <dd className="form-application-settings__description-list-description font-regular-16-19">
                  {replyToAddress}
                </dd>
              </>
            )}
          </dl>
        </>
      )}
    </div>
  );
}

export const FormApplicationSettingsDefaultView = asFetchableView(FormApplicationSettingsDefault);
export type FormApplicationSettingsDefaultView = ComponentProps<typeof FormApplicationSettingsDefaultView>;
